import React, { useState, useRef, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useLocation } from 'react-router-dom';
import { Button } from './ui/button';
import { Input } from './ui/input';
import {
  ChatCircle,
  X,
  PaperPlaneRight,
  Sparkle,
  ArrowCounterClockwise,
} from '@phosphor-icons/react';

const API = process.env.REACT_APP_BACKEND_URL;
const STORAGE_KEY_SESSION = 'tilly_session_id';

const WELCOME = {
  role: 'assistant',
  content: 'Hei! 👋 Jeg er **Tilly**, din personlige SEO-hjelper i Sortilize.\n\nJeg kan forklare begreper, lede deg gjennom funksjoner og svare på spørsmål om nettbutikken din. Prøv for eksempel:\n\n• *Hva er CTR?*\n• *Hvordan starter jeg AutoGuard?*\n• *Hva bør jeg fikse først?*',
};

const SUGGESTIONS = [
  'Hva er CTR?',
  'Hvordan starter jeg AutoGuard?',
  'Hva betyr +35 i potensial-kolonnen?',
  'Hvordan kobler jeg til Google?',
];

function renderMarkdown(text = '') {
  const escaped = text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  return escaped
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.+?)\*/g, '<em>$1</em>')
    .replace(/`([^`]+)`/g, '<code class="bg-muted px-1 rounded text-xs">$1</code>')
    .replace(/^- (.+)$/gm, '• $1')
    .replace(/\n/g, '<br/>');
}

export default function TillyWidget() {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [pulsed, setPulsed] = useState(false);
  const [messages, setMessages] = useState([WELCOME]);
  const [input, setInput] = useState('');
  const [sessionId, setSessionId] = useState(() => localStorage.getItem(STORAGE_KEY_SESSION) || null);
  const [sending, setSending] = useState(false);
  const [remaining, setRemaining] = useState(null);
  const [error, setError] = useState(null);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  const getHeaders = useCallback(() => ({ Authorization: `Bearer ${localStorage.getItem('token')}` }), []);

  // Pulse hint on first visit
  useEffect(() => {
    if (!localStorage.getItem('tilly_seen')) {
      const t = setTimeout(() => setPulsed(true), 3000);
      return () => clearTimeout(t);
    }
  }, []);

  // Scroll to bottom on new messages
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, sending]);

  // Focus input on open
  useEffect(() => {
    if (open) {
      setPulsed(false);
      localStorage.setItem('tilly_seen', '1');
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [open]);

  // Listen for external "open-tilly" events (from FAQ page, etc.)
  useEffect(() => {
    const handler = (e) => {
      setOpen(true);
      if (e.detail?.message) {
        setInput(e.detail.message);
      }
    };
    window.addEventListener('open-tilly', handler);
    return () => window.removeEventListener('open-tilly', handler);
  }, []);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || sending) return;
    setError(null);
    setInput('');
    setMessages((prev) => [...prev, { role: 'user', content: msg }]);
    setSending(true);
    try {
      const { data } = await axios.post(
        `${API}/api/help/chat`,
        { message: msg, session_id: sessionId, page_path: location.pathname },
        { headers: getHeaders() },
      );
      if (data.session_id && data.session_id !== sessionId) {
        setSessionId(data.session_id);
        localStorage.setItem(STORAGE_KEY_SESSION, data.session_id);
      }
      setRemaining(data.messages_remaining_today);
      setMessages((prev) => [...prev, { role: 'assistant', content: data.reply }]);
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (err.response?.status === 429) {
        setError(detail || 'Du har brukt opp dagens meldinger. Prøv igjen i morgen.');
      } else {
        setError(detail || 'Noe gikk galt. Prøv igjen om litt.');
      }
    } finally { setSending(false); }
  };

  const resetConversation = () => {
    setMessages([WELCOME]);
    setSessionId(null);
    localStorage.removeItem(STORAGE_KEY_SESSION);
    setError(null);
    setRemaining(null);
  };

  // Hide on public pages (landing, pricing, login) and onboarding
  const hiddenRoutes = ['/', '/pricing', '/login', '/onboarding', '/signup'];
  if (hiddenRoutes.some((r) => location.pathname === r)) return null;
  // Don't render if no auth token (user not logged in)
  if (!localStorage.getItem('token')) return null;

  return (
    <>
      {/* Floating launcher */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          className={`fixed bottom-6 right-6 z-40 flex items-center gap-2 px-4 py-3 bg-primary text-primary-foreground rounded-full shadow-lg hover:shadow-xl hover:scale-105 transition-all ${pulsed ? 'animate-pulse' : ''}`}
          data-testid="tilly-launcher"
          aria-label="Åpne Tilly"
        >
          <div className="relative">
            <ChatCircle size={22} weight="fill" />
            <Sparkle size={10} weight="fill" className="absolute -top-1 -right-1 text-warning" />
          </div>
          <span className="font-semibold pr-1">Spør Tilly</span>
        </button>
      )}

      {/* Chat panel */}
      {open && (
        <div
          className="fixed bottom-6 right-6 z-50 w-[380px] max-w-[calc(100vw-3rem)] h-[560px] max-h-[calc(100vh-3rem)] bg-background border border-border rounded-xl shadow-2xl flex flex-col overflow-hidden"
          data-testid="tilly-panel"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 bg-primary text-primary-foreground">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-primary-foreground/20 rounded-full flex items-center justify-center">
                <Sparkle size={16} weight="fill" />
              </div>
              <div>
                <p className="font-heading text-sm font-bold leading-tight">Tilly</p>
                <p className="text-xs opacity-80">Din SEO-hjelper</p>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={resetConversation}
                className="p-1.5 hover:bg-primary-foreground/20 rounded transition-colors"
                title="Ny samtale"
                data-testid="tilly-reset"
              >
                <ArrowCounterClockwise size={14} />
              </button>
              <button
                onClick={() => setOpen(false)}
                className="p-1.5 hover:bg-primary-foreground/20 rounded transition-colors"
                data-testid="tilly-close"
              >
                <X size={14} />
              </button>
            </div>
          </div>

          {/* Messages */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3 bg-muted/20">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div
                  className={`max-w-[85%] px-3 py-2 rounded-lg text-sm leading-relaxed ${
                    m.role === 'user'
                      ? 'bg-primary text-primary-foreground rounded-br-sm'
                      : 'bg-background border border-border rounded-bl-sm'
                  }`}
                  dangerouslySetInnerHTML={{ __html: renderMarkdown(m.content) }}
                />
              </div>
            ))}
            {sending && (
              <div className="flex justify-start">
                <div className="bg-background border border-border rounded-lg rounded-bl-sm px-3 py-2 text-sm">
                  <span className="inline-flex gap-1">
                    <span className="w-2 h-2 bg-muted-foreground rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></span>
                    <span className="w-2 h-2 bg-muted-foreground rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></span>
                    <span className="w-2 h-2 bg-muted-foreground rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></span>
                  </span>
                </div>
              </div>
            )}

            {/* Suggestion chips – only show before first user message */}
            {messages.length === 1 && !sending && (
              <div className="pt-2 space-y-1.5">
                <p className="text-xs text-muted-foreground">Forslag:</p>
                {SUGGESTIONS.map((s) => (
                  <button
                    key={s}
                    onClick={() => send(s)}
                    className="block w-full text-left text-xs px-3 py-2 bg-background border border-border rounded-md hover:border-primary hover:bg-primary/5 transition-colors"
                    data-testid={`tilly-suggestion-${s.slice(0, 10)}`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            )}

            {error && (
              <div className="bg-destructive/10 border border-destructive/20 text-destructive text-xs rounded-md px-3 py-2">
                {error}
              </div>
            )}
          </div>

          {/* Input */}
          <form
            onSubmit={(e) => { e.preventDefault(); send(); }}
            className="border-t border-border bg-background p-3 flex items-end gap-2"
          >
            <Input
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Spør meg noe…"
              className="text-sm"
              disabled={sending}
              data-testid="tilly-input"
            />
            <Button
              type="submit"
              size="sm"
              disabled={!input.trim() || sending}
              className="shrink-0"
              data-testid="tilly-send"
            >
              <PaperPlaneRight size={14} weight="bold" />
            </Button>
          </form>
          {remaining !== null && (
            <p className="text-[10px] text-muted-foreground text-center pb-1.5">
              {remaining} meldinger igjen i dag
            </p>
          )}
        </div>
      )}
    </>
  );
}
